import './AreaIndicatorWidgetModal.css';

import React, { useContext, useEffect, useState } from 'react';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import B2BStepper from '../../b2b-common/B2BStepper';
import DashboardSelector from '../components/DashboardSelector';
import WidgetCellSelector from '../components/WidgetCellSelector';
import { fetchAllDashboards, saveNewWidget } from '../../../services/b2b-reporting-apiServices';
import { LoginContext } from '../../../App';
import { B2BContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import { WidgetSizeSelector } from './EditWidgetModal';
import WidgetSelector from '../components/WidgetSelector';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import WidgetConfigurator from '../components/widget-configuration/WidgetConfigurator';

export const AreaIndicatorWidgetModalContext = React.createContext();

export function getFirstAvailableCell(dashboard) {
    const { rows, columns } = dashboard.dashboardConfig;
    const grid = Array.from({ length: rows }, () => Array(columns).fill(false));

    dashboard.widgets.forEach(widget => {
        const { row, column, width, height } = widget.widgetConfig;
        for (let r = row; r < row + height; r++) {
            for (let c = column; c < column + width; c++) {
                grid[r - 1][c - 1] = true; // Adjusting for 0-based index
            }
        }
    });

    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < columns; c++) {
            if (!grid[r][c]) {
                return { row: r + 1, col: c + 1 }; // Adjusting for 1-based index
            }
        }
    }

    return null; // No available cell found
}

const AreaIndicatorWidgetModal = ({ areaIndicator, open, onCancel, onSaved }) => {
    const { loginData } = useContext(LoginContext);
    const { setNotification } = useContext(B2BContentContext);
    const { t } = useB2BTranslation('b2b-reporting');

    const [page, setPage] = useState(1);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [dashboardsData, setDashboardsData] = useState({ content: [] });
    const [selectedWidgetConfig, setSelectedWidgetConfig] = useState(null);
    const [selectedWidgetTemplate, setSelectedWidgetTemplate] = useState(null);
    const [currentWidget, setCurrentWidget] = useState(null);

    const getAllDashboards = async () => {
        setIsSearching(true);
        try {
            const dashboards = await fetchAllDashboards(loginData.accessToken, loginData.loggedOrganizationUuid);
            setDashboardsData({
                ...dashboardsData,
                content: dashboards
            });
        } catch (error) {
            console.error('Error fetching dashboards:', error);
            showNotification(setNotification, t('Error fetching dashboards') + ': ' + error.message, 'error', 3000);
        } finally {
            setIsSearching(false);
        }
    }

    useEffect(() => {
        if (open) {
            getAllDashboards();
            setSelectedCell(null);
            setSelectedDashboard(null);
            setSelectedWidgetConfig(null);
            setSelectedWidgetTemplate(null);
            setCurrentWidget(null);
        }
    }, [open]);

    const handleSelectedWidgetTemplate = (widgetTemplate) => {
        setSelectedWidgetTemplate(widgetTemplate);
    }

    const handleSelectedWidgetConfig = (widgetConfig) => {
        setSelectedWidgetConfig(widgetConfig);
        setCurrentWidget(widgetConfig);
    }

    const handleSaveNewWidget = async () => {
        try {
            const savedDashboard = await saveNewWidget(loginData.accessToken, loginData.loggedOrganizationUuid, selectedDashboard.dashboardId, currentWidget);
            showNotification(setNotification, t('Widget saved successfully'), 'success', 3000);
            onSaved && onSaved(savedDashboard);
        } catch (error) {
            console.error('Error saving widget:', error);
            showNotification(setNotification, t('Error saving widget') + ': ' + error.message, 'error', 3000);
            onCancel && onCancel();
        }
    }

    const handleCancelNewWidget = () => {
        onCancel && onCancel();
    }

    const handleSelectedDashboard = (dashboard) => {
        setSelectedDashboard(dashboard);
        const availableCell = getFirstAvailableCell(dashboard);
        if (availableCell) {
            setCurrentWidget(prevWidget => ({
                ...prevWidget,
                widgetConfig: {
                    ...prevWidget.widgetConfig,
                    row: availableCell.row,
                    column: availableCell.col,
                    width: 1,
                    height: 1
                }
            }));
            setSelectedCell(availableCell);
        } else {
            setSelectedCell(null);
            setSelectedDashboard(null);
            showNotification(setNotification, t('No available cell found in the selected dashboard'), 'warning', 5000);
        }
    }

    const handleSelectedCell = (cell) => {
        setSelectedCell(cell);
        setCurrentWidget(prevWidget => ({
            ...prevWidget,
            widgetConfig: {
                ...prevWidget.widgetConfig,
                row: cell.row,
                column: cell.col,
                width: cell.width,
                height: cell.height
            }
        }));
    }

    const steps = [
        {
            label: t('Select Area Indicator Widget'),
            description: t('Select the area indicator widget you want to add to the dashboard.'),
            content: (
                <WidgetSelector
                    dto={areaIndicator}
                    scope={'AREA_INDICATOR'}
                    onSelectWidgetTemplate={handleSelectedWidgetTemplate}
                    selectedTemplate={selectedWidgetTemplate}
                />
            ),
            disableNext: (!selectedWidgetTemplate) ? true : false,
        },
        {
            label: t('Configure the selected widget'),
            description: t('Depending on the selected widget you will be required to configure some aspects of it.'),
            content: (
                <WidgetConfigurator
                    dto={areaIndicator}
                    widgetTemplate={selectedWidgetTemplate}
                    onWidgetConfigurationChange={handleSelectedWidgetConfig}
                />
            ),
            disableNext: (!selectedWidgetConfig) ? true : false,
        },
        {
            label: !selectedDashboard ? t('Select Dashboard') : t('Selected Dashboard') + `: ${selectedDashboard.dashboardName}`,
            description: t('Select the dashboard where you want to add the widget.'),
            content: (<DashboardSelector dashboards={dashboardsData} page={page}
                selectedDashboard={selectedDashboard} 
                setPage={setPage} onSelectedDashboard={handleSelectedDashboard} />),
            disableNext: (!selectedDashboard) ? true : false,
        },
        {
            label: t('Place the Widget'),
            description: t('Select the cell where you want to place the widget and also the size of it.'),
            content: (
                <div className='widget-placement'>
                    <WidgetSizeSelector widget={currentWidget} setWidget={setCurrentWidget} />
                    <WidgetCellSelector dashboard={selectedDashboard} widget={currentWidget} onSelectedCell={handleSelectedCell} />
                </div>
            ),
            disableNext: (!selectedCell) ? true : false,
        },
    ];

    return (
        <B2BPopUpForm
            open={open}
            title={t('Add Widget to Dashboard')}
            onClose={onCancel}
            className={'area-indicator-widget-modal b2b-light-01'}
        >
            <B2BStepper
                steps={steps}
                className={'area-indicator-widget-stepper'}
                onSuccess={handleSaveNewWidget}
                onCancel={handleCancelNewWidget}
            />
        </B2BPopUpForm>
    );
};

export default AreaIndicatorWidgetModal;